// AWS Cognito configuration for authentication
// Requirements: 8.1, 8.2

import { Amplify } from 'aws-amplify';

// Cognito configuration interface
export interface CognitoConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  identityPoolId?: string;
  mandatorySignIn?: boolean;
  cookieStorage?: {
    domain: string;
    path: string;
    expires: number;
    secure: boolean;
  };
}

// Default Cognito configuration
// These values should be set via environment variables in production
const cognitoConfig: CognitoConfig = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
  userPoolWebClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || '',
  identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
  mandatorySignIn: true,
  cookieStorage: {
    domain: process.env.REACT_APP_COOKIE_DOMAIN || 'localhost',
    path: '/',
    expires: 365,
    secure: process.env.NODE_ENV === 'production'
  }
};

// Configure Amplify with Cognito settings
export const configureCognito = (): void => {
  if (!cognitoConfig.userPoolId || !cognitoConfig.userPoolWebClientId) {
    console.warn('Cognito configuration is incomplete. Please set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_USER_POOL_CLIENT_ID environment variables.');
    return;
  }

  const authConfig: any = {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.userPoolWebClientId
    }
  };

  // Add identity pool if available
  if (cognitoConfig.identityPoolId) {
    authConfig.Cognito.identityPoolId = cognitoConfig.identityPoolId;
  }

  // Add OAuth if domain is available
  if (process.env.REACT_APP_COGNITO_DOMAIN) {
    authConfig.Cognito.loginWith = {
      oauth: {
        domain: process.env.REACT_APP_COGNITO_DOMAIN,
        scopes: ['email', 'openid', 'profile'],
        redirectSignIn: [process.env.REACT_APP_OAUTH_REDIRECT_SIGNIN || window.location.origin],
        redirectSignOut: [process.env.REACT_APP_OAUTH_REDIRECT_SIGNOUT || window.location.origin],
        responseType: 'code'
      }
    };
  }

  Amplify.configure({
    Auth: authConfig
  });
};

// Validate Cognito configuration
export const validateCognitoConfig = (): boolean => {
  const requiredFields = [
    'REACT_APP_AWS_REGION',
    'REACT_APP_COGNITO_USER_POOL_ID',
    'REACT_APP_COGNITO_USER_POOL_CLIENT_ID'
  ];

  const missingFields = requiredFields.filter(field => !process.env[field]);
  
  if (missingFields.length > 0) {
    console.error('Missing required Cognito environment variables:', missingFields);
    return false;
  }

  return true;
};

export default cognitoConfig;